import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Check, ArrowRight, Sparkles } from "lucide-react";

export default function SubscriptionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSubscribe = async () => {
    if (!user) {
      toast.error("Please sign in first");
      navigate('/login');
      return;
    }

    try {
      const response = await axios.post(`${API}/subscription/create-checkout`, {
        origin_url: window.location.origin
      }, { withCredentials: true });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    }
  };

  const freeFeatures = [
    "3 virtual try-ons",
    "Access to gallery",
    "Basic support"
  ];

  const proFeatures = [
    "50 AI generations per month",
    "Access to all designs",
    "Priority support",
    "Save unlimited results",
    "High-resolution exports",
    "Early access to new features"
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="subscription-page">
      <Navbar />
      
      <main className="pt-32 md:pt-40 pb-24">
        <div className="container-custom">
          {/* Header */}
          <div className="text-center mb-16">
            <p className="caption text-[#FF1493] mb-4">Pricing</p>
            <h1 className="heading-lg mb-6">Simple, Transparent Pricing</h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              Start with a free trial. Upgrade when you're ready to unlock 
              the full power of AI-powered virtual try-on.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="border border-white/10 bg-[#141414] p-8 md:p-10" data-testid="free-plan-card">
              <div className="mb-8">
                <p className="caption text-gray-500 mb-2">Free</p>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-5xl font-heading text-white">$0</span>
                </div>
                <p className="text-gray-400">Perfect for trying out TailorGlam</p>
              </div>

              <ul className="space-y-4 mb-8">
                {freeFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-white/10 flex items-center justify-center">
                      <Check className="w-3 h-3 text-gray-400" />
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {user ? (
                <Link to="/studio">
                  <button className="btn-secondary w-full">
                    Go to Studio
                  </button>
                </Link>
              ) : (
                <Link to="/signup" data-testid="free-signup-btn">
                  <button className="btn-secondary w-full">
                    Get Started Free
                  </button>
                </Link>
              )}
            </div>

            {/* Pro Plan */}
            <div className="border-2 border-[#FF1493] bg-[#141414] p-8 md:p-10 relative" data-testid="pro-plan-card">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FF1493] text-white px-4 py-1 font-mono text-xs uppercase tracking-widest">
                Most Popular
              </div>

              <div className="mb-8">
                <p className="caption text-[#FF1493] mb-2">Pro</p>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-5xl font-heading text-white">$12.99</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <p className="text-gray-400">For fashion enthusiasts and creators</p>
              </div>

              <ul className="space-y-4 mb-8">
                {proFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-[#FF1493]/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#FF1493]" />
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              {user?.is_subscribed ? (
                <button className="btn-primary w-full opacity-50 cursor-not-allowed" disabled>
                  <Check className="w-4 h-4" />
                  Currently Subscribed
                </button>
              ) : (
                <button 
                  onClick={handleSubscribe}
                  className="btn-primary w-full"
                  data-testid="subscribe-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  {user ? "Subscribe Now" : "Sign Up & Subscribe"}
                </button>
              )}
            </div>
          </div>

          {/* FAQ Preview */}
          <div className="mt-24 text-center">
            <h2 className="heading-md mb-8">Common Questions</h2>
            <div className="max-w-2xl mx-auto space-y-6 text-left">
              <div className="border-b border-white/10 pb-6">
                <h3 className="font-medium mb-2 text-white">How does the free trial work?</h3>
                <p className="text-gray-400 text-sm">
                  You get 3 free virtual try-ons to experience our AI technology. 
                  No credit card required. After that, you can subscribe to Pro for unlimited creativity.
                </p>
              </div>
              <div className="border-b border-white/10 pb-6">
                <h3 className="font-medium mb-2 text-white">Can I cancel anytime?</h3>
                <p className="text-gray-400 text-sm">
                  Yes! You can cancel your subscription at any time. Your access continues until the end of your billing period.
                </p>
              </div>
              <div className="border-b border-white/10 pb-6">
                <h3 className="font-medium mb-2 text-white">What happens to my generations if I cancel?</h3>
                <p className="text-gray-400 text-sm">
                  All your saved try-on results remain accessible in your account even after cancellation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
